import React from "react";
import { nanoid } from "nanoid";
import { Virtuoso } from "react-virtuoso";
import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  Col,
  Row,
  Input,
  Button,
  NMWaitIndicator,
  NMPopup,
} from "@neumetric/blueprint-lib/components";
import { useSelector } from "react-redux";
import { NMReportPage } from "@neumetric/blueprint-lib/pages";
import {
  getCurrentUser,
  getCurrentModule,
} from "@neumetric/blueprint-lib/store";
import {
  getControlTablesPageNo,
  performVariableMapping,
  generateControlPages,
  generateExceptionControlPages,
  waitForAllPagesRendered,
  extractCss,
  inlineImages,
} from "./helper";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";
import { DocumentTabs } from "../DocumentTabs";

const DefaultHeaderData = {
  reportId: "--",
  reportDate: "--",
  status: <span>--</span>,
  reportTitle: "--",
  reportFileName: "--",
  orgName: "--",
  department: "--",
  itemsOrder: [
    "reportId",
    "reportDate",
    "status",
    "reportTitle",
    "reportFileName",
  ],
};

export const Soc2Type2Report = forwardRef((props, ref) => {
  const { initialMode, reportData, currentAudit, updateReport, reloadReport } =
    props;
  const _initializing = useRef(false);
  const _pageData = useRef(reportData ? [...(reportData.content || [])] : []);
  const _pageDataForView = useRef([]);
  const _deletedPages = useRef([]);
  const _pageEditors = useRef([]);
  const _container = useRef(null);
  const _docTabRef = useRef(null);
  const _virtuosoRef = useRef(null);
  const _headerValueRef = useRef({});
  const currentModule = useSelector(getCurrentModule);
  const currentUser = useSelector(getCurrentUser);
  const [state, setState] = useState({
    pages:
      reportData && reportData.content
        ? reportData.content.map(() => nanoid(8))
        : [nanoid(8)],
    mode: initialMode || "edit",
    allowVirtualization: false,
    showOverlay: true,
  });
  const _pages = useRef(state.pages);

  useEffect(() => {
    if (!reportData || _initializing.current) {
      return;
    }
    initialize();
    _initializing.current = true;
  }, []);

  useEffect(() => {
    _pages.current = [...state.pages];
  }, [state.pages]);

  useEffect(() => {
    if (!state.showOverlay) {
      initTOCClick();
    }
  }, [state.showOverlay]);

  const initialize = async () => {
    const { config } = reportData;
    const parsedConfig = JSON.parse(config);
    if (!parsedConfig?.initialized) {
      await createInitialReport();
    } else {
      await updateAndLoadReport();
    }
  };

  const createInitialReport = useCallback(async () => {
    const updatedConfig = {};
    const { content, report_id = "", title = "" } = reportData;
    let contentCopy = [...content];
    _pageData.current = contentCopy;
    const emptyPageNo = _pageData.current.length;
    await createTempEmptyPage();
    if (!updatedConfig.variableMapping) {
      const result = getControlTablesPageNo(contentCopy);
      if (result) {
        let { controlTablePageNo, tocPages, expControlTablePageNo } = result;
        let extraPagesInserted = 0;

        if (controlTablePageNo > -1) {
          const newPages = await generateControlPages({
            controls: currentAudit.asmt_cntrls,
            startingPagNo: controlTablePageNo,
            emptyPageNo,
          });
          _pageData.current = [
            ..._pageData.current.slice(0, controlTablePageNo),
            ...newPages,
            ..._pageData.current.slice(controlTablePageNo + 1),
          ];
          extraPagesInserted = newPages.length - 1;
        }

        if (expControlTablePageNo > -1) {
          const adjustedExpPageNo = expControlTablePageNo + extraPagesInserted;
          const newPages = generateExceptionControlPages({
            controls: currentAudit.asmt_cntrls,
            startingPagNo: expControlTablePageNo,
            emptyPageNo,
          });
          _pageData.current = [
            ..._pageData.current.slice(0, adjustedExpPageNo),
            ...newPages,
            ..._pageData.current.slice(adjustedExpPageNo + 1),
          ];
        }
        _pageData.current = performVariableMapping(
          currentUser.org,
          currentAudit,
          reportData,
          _pageData.current,
        );
        _pageData.current = _pageData.current.slice(0, -1);
        updatedConfig.tocPages = tocPages;
      }
    }
    updatedConfig.variableMapping = true;
    if (!updatedConfig.reportValues) {
      const reportFileName =
        `${report_id}-${title || currentAudit.title || ""}`.replace(
          /\s+/g,
          "-",
        );
      updatedConfig.reportValues = {
        orgName: currentUser.org.brandname,
        status: "Draft",
        reportTitle: `${currentAudit.title}`,
        reportFileName: reportFileName,
        reportId: report_id,
        reportDate: "-",
        orgLogo: currentUser.org.logo_url,
        reportYear: new Date().getFullYear(),
        infosecEmail: currentUser.org.infosec_email,
      };
    }
    updatedConfig.initialized = true;
    const updateData = {
      content: _pageData.current,
      config: JSON.stringify(updatedConfig),
    };
    const options = { onComplete: handleUpdateComplete };
    updateReport(updateData, options);
  }, [state.pages]);

  const createTempEmptyPage = useCallback(() => {
    handleAddPage(_pageData.current.length - 1);
    return new Promise((iResolve) => {
      setTimeout(iResolve, 3000);
    });
  }, [state.pages]);

  const handleUpdateComplete = () => {
    reloadReport();
  };

  const updateAndLoadReport = async () => {
    await handleGenerateTableOfContents();
    setState((_) => ({ ..._, showOverlay: false, allowVirtualization: true }));
  };

  const getHeaderValues = useCallback(() => {
    const config = getConfig();
    let headerValues = {};
    if (config.reportValues) {
      const status = reportData.status.toLowerCase();
      const statusLabel = status === "draft" ? "Draft" : "Approved";
      const className = status === "draft" ? "text-emergency" : "text-closed";
      headerValues = { ...config.reportValues, ..._headerValueRef.current };
      headerValues.status = <span className={className}>{statusLabel}</span>;
      const reportDate =
        reportData && reportData.reportdate
          ? new DateTimeUtil(reportData.reportdate).toString("ddd, DD-MMM-YYYY")
          : "-";
      headerValues.reportDate = reportDate;
      headerValues.orgLogo = currentUser.org.logo_url;
      return headerValues;
    }
    return DefaultHeaderData;
  }, [reportData]);

  const getFooterValues = useCallback(() => {
    const config = getConfig();
    const footerData = {
      showPageNo: true,
      copyright: (
        <span>
          Controlled Master of this document exists within the internal network
          of{" "}
          {config.reportValues ? config.reportValues.orgName : "[[ORG_NAME]]"}.
          Printed copies, standalone versions in PDF or other file formats &
          copies outside this network are considered
          <span className="ml-2 mr-2 text-caution fw-600">
            "Uncontrolled"
          </span>{" "}
          & may not be up-to-date.
        </span>
      ),
      infosec: (
        <span>
          Uncontrolled copies of this document should not be distributed freely.
          Contact{" "}
          {config.reportValues ? config.reportValues.orgName : "[[ORG_NAME]]"}{" "}
          at{" "}
          {config.reportValues
            ? config.reportValues.infosecEmail
            : "[[INFOSEC_EMAIL]]"}{" "}
          to obtain the latest version of this document.
        </span>
      ),
      showOrgLogo: true,
      showModuleLogo: true,
    };
    return footerData;
  }, [reportData]);

  const handleAddPage = useCallback((pageNo) => {
    _pageData.current.splice(pageNo + 1, 0, "");
    setState((prev) => {
      const pages = [...prev.pages];
      pages.splice(pageNo + 1, 0, nanoid(8));
      return { ...prev, pages };
    });
  }, []);

  const handleRemovePage = useCallback(
    (pageNo) => {
      _pageData.current.splice(pageNo, 1);
      const pages = state.pages.filter((_, index) => index !== pageNo);
      _deletedPages.current.push(Number(pageNo));
      setState((_) => ({ ..._, pages: pages }));
    },
    [state.pages],
  );

  const handlePageChange = (pageNo, value) => {
    _pageData.current[pageNo] = value;
  };

  const handleInstanceReady = (pageNo, event) => {
    _pageEditors.current[pageNo] = event.editor;
  };

  const getTableOfContentsId = (title, pageNo, index) => {
    const slug = (title || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return `toc-${slug || "section"}-${pageNo}-${index + 1}`;
  };

  const getPageHtmlForExport = (pageNo) => {
    // Get the full rendered page element from DOM
    const pageElement = _container.current.querySelector(`#page-${pageNo + 1}`);
    if (!pageElement) return "";

    // Clone entire page (header + content + footer)
    const clone = pageElement.cloneNode(true);

    // Find ck-container inside the clone and inject real CKEditor content
    const ckContainer = clone.querySelector(".ck-container");
    if (ckContainer && _pageDataForView.current[pageNo]) {
      ckContainer.innerHTML = _pageDataForView.current[pageNo];
    }

    return clone.outerHTML;
  };

  const setPageDataForView = () => {
    const pages = document.querySelectorAll(".nm-report-page");
    pages.forEach((page, index) => {
      const container = page.querySelector(".ck-container");
      _pageDataForView.current[index] = container.innerHTML;
    });
  };

  const generateTableOfContents = (tableOfContents) => {
    const tocEntriesPerPage = 34;
    if (tableOfContents.length > 0) {
      const handleTocLinkClick = (event, targetId) => {
        event.preventDefault();
        const target = _container.current?.querySelector(`#${targetId}`);

        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      };

      const createTocWrapper = (items) => {
        const tocWrapper = document.createElement("div");
        items.forEach((item) => {
          const row = document.createElement("div");
          const isFirstLevel = Number(item.level) === 0;
          const fontSize = isFirstLevel ? "fs-17" : "fs-15";
          const fontWeight = isFirstLevel ? "fw-700" : "fw-500";
          row.classList.add(
            "d-flex",
            "flex-row",
            "align-center",
            "justify-between",
            "line-auto",
            `pl-[${item.level * 2}rem]`,
            `mt-3`,
            "mb-3",
          );
          const titleLink = document.createElement("a");
          titleLink.href = `#${item.id}`;
          titleLink.classList.add(
            fontSize,
            "text-action",
            fontWeight,
            "toc-item",
          );
          titleLink.style.textDecoration = "none";
          titleLink.textContent = item.title;
          titleLink.setAttribute("data-page", item.pageNo);

          const pageLink = document.createElement("a");
          pageLink.href = `#${item.pageId}`;
          pageLink.classList.add(fontSize, "text-action", fontWeight);
          pageLink.style.textDecoration = "none";
          pageLink.textContent = item.pageNo;
          pageLink.addEventListener("click", (event) =>
            handleTocLinkClick(event, item.pageId),
          );

          row.appendChild(titleLink);
          row.appendChild(pageLink);
          tocWrapper.appendChild(row);
        });

        return tocWrapper;
      };

      const tocChunks = [];
      for (
        let index = 0;
        index < tableOfContents.length;
        index += tocEntriesPerPage
      ) {
        tocChunks.push(tableOfContents.slice(index, index + tocEntriesPerPage));
      }

      const placeholderNodes = [];
      const walker = document.createTreeWalker(
        _container.current,
        NodeFilter.SHOW_TEXT,
        null,
        false,
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.includes("[[TableOfContents]]")) {
          placeholderNodes.push(node);
        }
      }

      placeholderNodes.forEach((placeholderNode, index) => {
        const parent = placeholderNode.parentNode;
        if (index === 0) {
          parent.id = "toc-container";
        }
        const newNode = document.createElement("span");
        const chunk = tocChunks[index];
        if (chunk?.length) {
          newNode.appendChild(createTocWrapper(chunk));
        }
        parent.replaceChild(newNode, placeholderNode);
      });
    }
  };

  const getTableOfContentsItems = () => {
    // setState((_) => ({ ..._, mode: "view" }));
    return new Promise((resolve) => {
      setTimeout(() => {
        if (_container.current) {
          const allSectionTitles =
            _container.current.querySelectorAll(".nm-report-title");
          const tableOfContents = [];
          allSectionTitles.forEach((sectionTitle, index) => {
            const level = sectionTitle.getAttribute("level");
            const pageContainer = sectionTitle.closest("[data-pageno]");
            const title = sectionTitle.textContent?.trim();

            if (pageContainer) {
              const pageNo = pageContainer.getAttribute("data-pageno");
              const sectionId = getTableOfContentsId(title, pageNo, index);
              const pageId = `page-${pageNo}`;
              pageContainer.id = pageId;
              sectionTitle.id = sectionId;
              sectionTitle.setAttribute("data-toc-id", sectionId);
              tableOfContents.push({
                id: sectionId,
                element: sectionTitle,
                pageId,
                title,
                pageNo,
                level: level,
              });
            }
          });
          resolve(tableOfContents);
        }
      }, 1000);
    });
  };

  const getPageSanitizedContent = (iPageNo) => {
    let content = _pageData.current[iPageNo] || "";
    if (state.mode === "view") {
      content = _pageDataForView.current[iPageNo] || content;
    }
    return content.replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " ");
  };

  const getPageContent = (pageNo) => {
    const headerValues = getHeaderValues();
    const pageHTML = getPageSanitizedContent(pageNo);
    return (
      <Col className="w-100 h-100">
        {pageNo === 0 && (
          <Col className="w-100 mt-40" align="center" justify="center">
            <Row className="mb-16 w-100" align="center" justify="center">
              <img
                className="w-280-px h-60-px report-org-logo"
                src={headerValues.orgLogo}
                alt="Organization Logo"
              />
            </Row>
            <Col
              className="mb-16 fs-32 fw-600 text-heading"
              align="center"
              justify="center"
            >
              {headerValues.orgName}
            </Col>
            <Col
              className="fs-32 fw-600 text-heading mb-16 text-center"
              align="center"
              justify="center"
            >
              {headerValues.reportTitle}
            </Col>
            <Col
              className="fs-32 fw-600 text-heading line-auto mb-20"
              align="center"
              justify="center"
            >
              {headerValues.reportYear}
            </Col>
            <Col className="w-100 mb-8" align="end" justify="center">
              <Row align="center">
                <span className="fs-12 fw-400 text-subtitle">
                  Classification:
                </span>
                <Col className="ml-4 bg-notification-warning pl-8 pr-8 pt-1 pb-1">
                  <span className="fs-15 text-contrast fw-700">Restricted</span>
                </Col>
              </Row>
            </Col>
          </Col>
        )}
        <Col className="w-100 flex-auto ">
          {state.mode === "view" ? (
            <div className={"ff-exo2 ck-container w-100 mb-8 pa-1"}>
              <span dangerouslySetInnerHTML={{ __html: pageHTML }}></span>
            </div>
          ) : (
            <Input
              toolbarType="inline"
              classes={{
                input: "h-100 pl-0",
                root: "w-100 h-100",
              }}
              attribute={`page_${pageNo}`}
              typeRepresentation="HTML"
              value={pageHTML}
              config={{ allowedContent: true }}
              onChange={handlePageChange.bind(null, pageNo)}
              onInstanceReady={handleInstanceReady.bind(null, pageNo)}
            />
          )}
        </Col>
        {state.mode === "edit" && (
          <Row className="pos-absolute " style={{ bottom: 8, left: 0 }}>
            <Button
              label="Add Page"
              classes={{ button: "mr-10" }}
              onClick={() => handleAddPage(pageNo)}
            />
            {pageNo > 0 && (
              <Button
                label="Remove This Page"
                classes={{ button: "mr-10" }}
                onClick={() => handleRemovePage(pageNo)}
              />
            )}
          </Row>
        )}
      </Col>
    );
  };

  const getConfig = () => {
    return JSON.parse(reportData.config);
  };

  const handleGenerateTableOfContents = async () => {
    const tocContents = await getTableOfContentsItems();
    _docTabRef.current.setItems(tocContents);
    generateTableOfContents(tocContents);
    setPageDataForView();
  };

  const initTOCClick = () => {
    const handleTOCClick = (e) => {
      const anchor = e.target.closest("a.toc-item");
      const content = e.target.id === "toc-content-item" ? e.target : null;
      if (!anchor && !content) return;

      if (content) {
        const config = getConfig();
        if (Array.isArray(config.tocPages) && config.tocPages.length > 0) {
          const page = config.tocPages[0];
          _virtuosoRef.current?.scrollToIndex({
            index: page,
            align: "start",
            behavior: "smooth",
          });
          return;
        }
      }
      e.preventDefault();

      const pageNo = anchor.getAttribute("data-page");
      const tocId = anchor.getAttribute("href")?.replace("#", "");

      if (!pageNo || !tocId) return;

      const index = parseInt(pageNo, 10) - 1;
      if (isNaN(index) || index < 0) return;

      // Step 1 — scroll Virtuoso to that page
      _virtuosoRef.current?.scrollToIndex({
        index,
        align: "start",
        behavior: "smooth",
      });

      // Step 2 — once page is rendered, scroll to the section element
      const scrollToElement = () => {
        const target = document.querySelector(`[data-toc-id="${tocId}"]`);

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // Page not rendered yet — retry
          setTimeout(scrollToElement, 100);
        }
      };

      // Small delay to let Virtuoso render the page first
      setTimeout(scrollToElement, 300);
    };

    document.addEventListener("click", handleTOCClick);
    return () => document.removeEventListener("click", handleTOCClick);
  };

  const removeTOC = () => {
    const config = getConfig();
    if (config.tocPages) {
      config.tocPages.forEach((pageNo) => {
        const index = _deletedPages.current.indexOf(Number(pageNo));
        if (index === -1) {
          _pageData.current[pageNo] = reportData.content[pageNo];
        }
      });
    }
  };

  const handleSetMode = (mode) => {
    if (mode === "edit") {
      _deletedPages.current = [];
      removeTOC();
    }
    setState((_) => ({ ..._, mode: mode }));
  };

  const getReportData = () => {
    removeTOC();
    const config = getConfig();
    const data = {};
    data.content = _pageData.current;
    config.reportValues = {
      ...config.reportValues,
      ..._headerValueRef.current,
    };
    data.config = JSON.stringify(config);
    return data;
  };

  const getContentForExport = async () => {
    setState((_) => ({ ..._, allowVirtualization: false }));
    await waitForAllPagesRendered(
      _container.current,
      _pageDataForView.current.length,
    );
    const pages = await Promise.all(
      state.pages.map((_, index) => {
        const html = getPageHtmlForExport(index);
        return inlineImages(html); // inline S3 images
      }),
    );
    setState((_) => ({ ..._, allowVirtualization: true }));
    // 6. Extract CSS
    const css = extractCss();

    return { pages, css };
  };

  useImperativeHandle(ref, () => ({
    getData: getReportData,
    setMode: handleSetMode,
    getContentForExport: getContentForExport,
  }));

  const handleHeaderValueChange = (values) => {
    _headerValueRef.current = { ..._headerValueRef.current, ...values };
  };

  const renderPage = (pageKey, index) => {
    const firstPage = index === 0;
    return (
      <NMReportPage
        orgLogo={currentUser.org.logo_url}
        moduleLogo={currentModule.logo}
        key={pageKey}
        pageNo={index + 1}
        totalPages={state.pages.length}
        header={firstPage ? undefined : getHeaderValues()}
        classes={{
          content: "nm-report-page-content pos-relative",
        }}
        footer={getFooterValues()}
        editHeader={state.mode === "edit"}
        editableHeaderFields={["orgName", "reportFileName", "reportTitle"]}
        onHeaderValueChange={handleHeaderValueChange}
        hiddenHeaderFields={["department"]}
      >
        {getPageContent(index)}
      </NMReportPage>
    );
  };

  const handleTocSelect = (node) => {
    const { pageNo, id } = node || {};
    const index = parseInt(pageNo, 10) - 1;
    if (isNaN(index) || index < 0) return;

    // Step 1 — scroll Virtuoso to that page
    _virtuosoRef.current?.scrollToIndex({
      index,
      align: "start",
      behavior: "smooth",
    });

    if (state.mode === "view") {
      const scrollToElement = () => {
        const target = document.querySelector(`[data-toc-id="${id}"]`);

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // Page not rendered yet — retry
          setTimeout(scrollToElement, 100);
        }
      };
      // Small delay to let Virtuoso render the page first
      setTimeout(scrollToElement, 1000);
    }
  };

  return (
    <Row
      className="w-100 h-100 overflow-hidden pos-relative"
      align="start"
      justify="center"
    >
      <Col className="h-100 overflow-hidden mr-48">
        <DocumentTabs ref={_docTabRef} items={[]} onSelect={handleTocSelect} />
      </Col>
      <Col className="h-100 w-830-px overflow-auto pa-20" ref={_container}>
        {state.allowVirtualization ? (
          <Virtuoso
            ref={_virtuosoRef}
            data={state.pages}
            customScrollParent={_container.current}
            itemContent={(index, pageKey) => renderPage(pageKey, index)}
          />
        ) : (
          state.pages.map((pageKey, index) => renderPage(pageKey, index))
        )}
      </Col>
      {state.showOverlay && (
        <NMPopup visible>
          <NMWaitIndicator message="Loading Report. Please wait..." />
        </NMPopup>
      )}
    </Row>
  );
});
